/**
 * @file index.js
 * @version 1.5.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-02-02
 * @modified 2026-10-30
 *
 * Widget Registry - Central configuration for all dashboard widgets.
 * Widgets may opt into admin-only visibility via `adminOnly: true`; the
 * configurator (see DashboardConfigurator.getUnplacedWidgets) hides those
 * from non-admin users.
 *
 * 1.4.0 (2026-10-15, Cam): the Data Checks widget (data_quality) is retired —
 * its contact checks are the contact_data_checks notice, in the Notices widget
 * and the status bar's Alerts. Saved layouts lose it once, with Notices in its
 * place where Notices was not already there (utils/dashboardLayoutUpgrade.js
 * on the server, data_checks_retired_2026_10).
 *
 * 1.5.0 (2026-10-30, Cam — 29 Oct): rep_order_requests ("Request an order") —
 * a rep asks the office for an order, a standing order or a change; first on
 * the rep's phone, a strip above the grid on their laptop.
 */

// Widget component imports (lazy loaded for performance)
import ClockWidget from './ClockWidget';
import CalendarWidget from './CalendarWidget';
import XeroStatusWidget from './XeroStatusWidget';
import EmailCoverageWidget from './EmailCoverageWidget';
import NoticesWidget from './NoticesWidget';
import DeliveryHeatmapWidget from './DeliveryHeatmapWidget';
import CustomerChartWidget from './CustomerChartWidget';
import TodaysDeliveriesWidget from './TodaysDeliveriesWidget';
import StandingOrdersWidget from './StandingOrdersWidget';
import SpacerWidget from './SpacerWidget';
import WorkerCronLogsWidget from './WorkerCronLogsWidget';
import RepYourOrdersWidget from './RepYourOrdersWidget';
import RepTonnesTrendWidget from './RepTonnesTrendWidget';
import RepTopCustomersWidget from './RepTopCustomersWidget';
import RepLapsedAccountsWidget from './RepLapsedAccountsWidget';
import RepOrderRequestWidget from './RepOrderRequestWidget';

/**
 * Widget Registry
 * Each widget is self-describing with metadata for the configurator
 *
 * Column width types:
 * - 'narrow': 1fr proportional width columns
 * - 'medium': flex columns with moderate width
 * - 'wide': flex columns with larger width
 */
export const widgetRegistry = {
  clock: {
    id: 'clock',
    name: 'Analog Clock',
    icon: 'schedule',
    component: ClockWidget,
    minWidth: 'narrow',
    defaultFlex: null,
    allowedColumns: ['narrow', 'medium', 'wide'],
    canSpan: true,
    hasSettings: false,
    description: 'Displays current time with analog clock face'
  },
  calendar: {
    id: 'calendar',
    name: 'Calendar',
    icon: 'calendar_month',
    component: CalendarWidget,
    minWidth: 'narrow',
    defaultFlex: null,
    allowedColumns: ['narrow', 'medium', 'wide'],
    canSpan: true,
    hasSettings: false,
    description: 'Month view calendar with the shared office calendar\'s events'
  },
  xero_status: {
    id: 'xero_status',
    name: 'Xero Connection',
    icon: 'link',
    component: XeroStatusWidget,
    minWidth: 'narrow',
    defaultFlex: null,
    allowedColumns: ['narrow', 'medium', 'wide'],
    canSpan: false,
    hasSettings: false,
    description: 'Shows Xero connection status with reconnect options'
  },
  email_coverage: {
    id: 'email_coverage',
    name: 'Email Coverage',
    icon: 'mail',
    component: EmailCoverageWidget,
    minWidth: 'narrow',
    defaultFlex: null,
    allowedColumns: ['narrow', 'medium', 'wide'],
    canSpan: true,
    hasSettings: false,
    description: 'Circular chart showing contact email coverage'
  },
  // Notices (Cam, 14 Oct 2026) took Email Coverage's place on the default
  // layouts and on every standard user's saved one (utils/dashboardLayoutUpgrade.js
  // on the server). What it shows is decided per person by the server
  // (config/noticeRegistry.js); reps are kept out here and there.
  notices: {
    id: 'notices',
    name: 'Notices',
    icon: 'campaign',
    component: NoticesWidget,
    minWidth: 'narrow',
    defaultFlex: null,
    allowedColumns: ['narrow', 'medium', 'wide'],
    canSpan: true,
    hasSettings: false,
    badges: ['ADMIN', 'USER'],
    description: 'Things needing attention for your role — e.g. orders not invoiced in a month whose statements have gone out'
  },
  delivery_heatmap: {
    id: 'delivery_heatmap',
    name: 'Delivery Heatmap',
    icon: 'map',
    component: DeliveryHeatmapWidget,
    minWidth: 'wide',
    defaultFlex: 50,
    allowedColumns: ['wide'],  // RESTRICTED - only wide columns
    canSpan: true,
    hasSettings: false,
    description: '3-month delivery heatmap visualization'
  },
  customer_chart: {
    id: 'customer_chart',
    name: 'New Customer Chart',
    icon: 'show_chart',
    component: CustomerChartWidget,
    minWidth: 'medium',
    defaultFlex: 33,
    allowedColumns: ['medium', 'wide'],
    canSpan: true,
    hasSettings: false,
    description: 'Line chart showing new customer procurement'
  },
  todays_deliveries: {
    id: 'todays_deliveries',
    name: "Today's Deliveries",
    icon: 'local_shipping',
    component: TodaysDeliveriesWidget,
    minWidth: 'medium',
    defaultFlex: 50,
    allowedColumns: ['medium', 'wide'],
    canSpan: true,
    hasSettings: true,
    settingsSchema: {
      zoom: { type: 'number', min: 9, max: 18, default: 12 }
    },
    description: "Table of today's scheduled deliveries"
  },
  standing_orders: {
    id: 'standing_orders',
    name: 'Standing Orders',
    icon: 'event_repeat',
    component: StandingOrdersWidget,
    minWidth: 'medium',
    defaultFlex: null,
    allowedColumns: ['medium', 'wide'],
    canSpan: true,
    hasSettings: true,
    // Reps get it too, on their fixed layout. The list it shows is scoped to
    // their own accounts by the search route's scopeCondition, so a rep sees
    // their patch's repeat orders and nobody else's — it is the same widget,
    // not a second one. Gone Quiet answers a different question (who has
    // stopped buying anything at all).
    badges: ['ADMIN', 'USER', 'REP'],
    description: 'The week\'s standing orders by day, with when each customer last had the product'
  },
  worker_cron_logs: {
    id: 'worker_cron_logs',
    name: 'Worker Cron Logs',
    icon: 'terminal',
    component: WorkerCronLogsWidget,
    minWidth: 'medium',
    defaultFlex: 50,
    allowedColumns: ['medium', 'wide'],
    canSpan: true,
    hasSettings: false,
    adminOnly: true,
    description: 'Terminal-style log viewer for scheduled worker jobs (admin only)'
  },
  // ── Rep widgets ───────────────────────────────────────────────────────────
  // badges: ['REP'] keeps these out of everyone else's configurator. They are
  // placed by config/repDashboardLayout.js on the server, never by a user, so
  // hasSettings is false throughout — a rep cannot persist widget settings
  // (routes/dashboard.js refuses with LAYOUT_LOCKED), and offering a control
  // that silently fails is worse than not offering it.
  rep_your_orders: {
    id: 'rep_your_orders',
    name: 'Your Orders',
    icon: 'event_available',
    component: RepYourOrdersWidget,
    minWidth: 'wide',
    defaultFlex: 50,
    allowedColumns: ['wide'],
    canSpan: true,
    hasSettings: false,
    badges: ['REP'],
    description: "What is going out to your customers, by delivery date"
  },
  rep_tonnes_trend: {
    id: 'rep_tonnes_trend',
    name: 'Tonnes Sold',
    icon: 'monitoring',
    component: RepTonnesTrendWidget,
    minWidth: 'medium',
    defaultFlex: 33,
    allowedColumns: ['medium', 'wide'],
    canSpan: true,
    hasSettings: false,
    badges: ['REP'],
    description: 'Your tonnage by month, against the same period last year'
  },
  rep_top_customers: {
    id: 'rep_top_customers',
    name: 'Top Customers',
    icon: 'leaderboard',
    component: RepTopCustomersWidget,
    minWidth: 'narrow',
    defaultFlex: 25,
    allowedColumns: ['narrow', 'medium', 'wide'],
    canSpan: true,
    hasSettings: false,
    badges: ['REP'],
    description: 'Your biggest accounts by tonnage'
  },
  // "Request an order" (Cam, 29 Oct 2026): first on the rep's phone; on a
  // laptop a strip above the grid (top_strip), never a grid cell — the grid's
  // two rows stay as they are.
  rep_order_requests: {
    id: 'rep_order_requests',
    name: 'Request an order',
    icon: 'add_shopping_cart',
    component: RepOrderRequestWidget,
    minWidth: 'wide',
    defaultFlex: null,
    allowedColumns: ['wide'],
    canSpan: false,
    hasSettings: false,
    badges: ['REP'],
    description: 'Ask the office for an order, a standing order, or a change to one'
  },
  rep_lapsed_accounts: {
    id: 'rep_lapsed_accounts',
    name: 'Gone Quiet',
    icon: 'phone_missed',
    component: RepLapsedAccountsWidget,
    minWidth: 'narrow',
    defaultFlex: 25,
    allowedColumns: ['narrow', 'medium', 'wide'],
    canSpan: true,
    hasSettings: false,
    badges: ['REP'],
    description: 'Accounts that used to order and have not lately'
  },
  ...Object.fromEntries(
    Array.from({ length: 10 }, (_, i) => [`spacer_${i + 1}`, {
      id: `spacer_${i + 1}`,
      name: `Spacer ${i + 1}`,
      icon: 'expand',
      component: SpacerWidget,
      minWidth: 'narrow',
      defaultFlex: null,
      allowedColumns: ['narrow', 'medium', 'wide'],
      canSpan: false,
      hasSettings: false,
      isSpacer: true,
      description: 'Empty spacer for layout spacing'
    }])
  )
};

/**
 * Layout Presets
 * Defines the column structure for each layout option
 */
export const layoutPresets = {
  'narrow-left-two-right': {
    id: 'narrow-left-two-right',
    name: 'Narrow Left + 2 Wide',
    description: 'Narrow sidebar on left, two equal columns on right',
    columns: [
      { type: 'narrow', width: '1fr' },
      { type: 'wide', width: '2fr' },
      { type: 'wide', width: '2fr' }
    ],
    gridTemplate: '1fr 2fr 2fr'
  },
  'two-left-narrow-right': {
    id: 'two-left-narrow-right',
    name: '2 Wide + Narrow Right',
    description: 'Two equal columns on left, narrow sidebar on right',
    columns: [
      { type: 'wide', width: '2fr' },
      { type: 'wide', width: '2fr' },
      { type: 'narrow', width: '1fr' }
    ],
    gridTemplate: '2fr 2fr 1fr'
  },
  'three-equal': {
    id: 'three-equal',
    name: 'Three Equal',
    description: 'Three equal-width columns',
    columns: [
      { type: 'medium', width: '1.5fr' },
      { type: 'medium', width: '1.5fr' },
      { type: 'medium', width: '1.5fr' }
    ],
    gridTemplate: '1.5fr 1.5fr 1.5fr'
  },
  'narrow-middle-wide-sides': {
    id: 'narrow-middle-wide-sides',
    name: 'Wide - Narrow - Wide',
    description: 'Narrow column in the middle, wide columns on sides',
    columns: [
      { type: 'wide', width: '2fr' },
      { type: 'narrow', width: '1fr' },
      { type: 'wide', width: '2fr' }
    ],
    gridTemplate: '2fr 1fr 2fr'
  },
  // The Rep dashboard's shape. Deliberately its own id rather than reusing
  // two-left-narrow-right: it must never read as a choice a user made, and the
  // server's validPresets list excludes it so nobody can select it.
  'rep-fixed': {
    id: 'rep-fixed',
    name: 'Rep (fixed)',
    description: 'The fixed rep layout — set in config/repDashboardLayout.js',
    // Three equal columns. The third was 1fr against two 2fr columns, and Gone
    // Quiet — a list of company names, how long they have been quiet and what
    // they used to be worth — had nowhere near the width for it.
    columns: [
      { type: 'wide', width: '1fr' },
      { type: 'wide', width: '1fr' },
      { type: 'wide', width: '1fr' }
    ],
    gridTemplate: '1fr 1fr 1fr'
  },
  'two-equal': {
    id: 'two-equal',
    name: 'Two Equal',
    description: 'Two equal-width columns',
    columns: [
      { type: 'wide', width: '1fr' },
      { type: 'wide', width: '1fr' }
    ],
    gridTemplate: '1fr 1fr'
  }
};

/**
 * Default widget placement for each layout preset
 */
export const defaultPlacements = {
  'narrow-left-two-right': {
    column_0: ['clock', 'calendar', 'xero_status', 'notices'],
    column_1: ['delivery_heatmap', 'customer_chart'],
    column_2: ['todays_deliveries']
  },
  'two-left-narrow-right': {
    column_0: ['todays_deliveries'],
    column_1: ['notices'],
    column_2: ['clock', 'calendar']
  },
  'three-equal': {
    column_0: ['clock', 'calendar', 'notices'],
    column_1: ['customer_chart', 'todays_deliveries'],
    column_2: ['xero_status']
  },
  'narrow-middle-wide-sides': {
    column_0: ['delivery_heatmap', 'todays_deliveries'],
    column_1: ['clock', 'calendar', 'xero_status', 'notices'],
    column_2: ['customer_chart']
  },
  'two-equal': {
    column_0: ['clock', 'calendar', 'delivery_heatmap', 'customer_chart'],
    column_1: ['xero_status', 'notices', 'todays_deliveries']
  }
};

/**
 * Column width presets for custom layouts
 */
export const columnWidthOptions = [
  { label: 'Narrow', value: '1fr', type: 'narrow' },
  { label: 'Standard', value: '1fr', type: 'medium' },
  { label: 'Wide', value: '2fr', type: 'wide' }
];

/**
 * Resolve the active layout configuration from settings.
 * Handles both named presets and custom layouts.
 */
export const resolveLayout = (settings) => {
  if (settings.layout_preset === 'custom') {
    const customCols = settings.widget_placement?.custom_columns;
    if (customCols && customCols.length > 0) {
      return {
        id: 'custom',
        name: 'Custom',
        columns: customCols,
        gridTemplate: customCols.map(c => c.width).join(' ')
      };
    }
  }
  return layoutPresets[settings.layout_preset] || layoutPresets['narrow-left-two-right'];
};

/**
 * Whether a widget should be offered to / rendered for this user.
 *
 * Honours both the older `adminOnly` boolean and the newer `badges` list.
 * adminOnly is deliberately NOT migrated — worker_cron_logs is its only user
 * and churning it buys nothing; this collapses the two into one predicate so
 * call sites never test either flag directly.
 *
 * @param {object} widget  a widgetRegistry entry
 * @param {object} viewer  { badge, isAdmin } — badgeOf(user) and user.is_admin
 */
export const isWidgetVisibleTo = (widget, { badge, isAdmin } = {}) => {
  if (!widget) return false;
  if (widget.adminOnly && !isAdmin) return false;
  if (widget.badges && !widget.badges.includes(badge)) return false;
  return true;
};

/**
 * Get all available widgets as an array
 */
export const getWidgetList = () => Object.values(widgetRegistry);

/**
 * Get a widget by ID
 */
export const getWidget = (id) => widgetRegistry[id];

/**
 * Check if a widget can be placed in a column of given type
 */
export const canPlaceWidget = (widgetId, columnType) => {
  const widget = widgetRegistry[widgetId];
  if (!widget) return false;
  return widget.allowedColumns.includes(columnType);
};

/**
 * Get widgets that can be placed in a column type
 */
export const getWidgetsForColumnType = (columnType) => {
  return Object.values(widgetRegistry).filter(w => w.allowedColumns.includes(columnType));
};

/**
 * Validate widget placement against layout
 * Returns { valid: boolean, errors: string[] }
 */
export const validatePlacement = (layoutPreset, widgetPlacement) => {
  const errors = [];
  const layout = layoutPresets[layoutPreset];

  if (!layout) {
    return { valid: false, errors: ['Invalid layout preset'] };
  }

  // Check each column
  Object.entries(widgetPlacement).forEach(([columnKey, widgets]) => {
    const columnIndex = parseInt(columnKey.replace('column_', ''));
    const column = layout.columns[columnIndex];

    if (!column) {
      errors.push(`Column ${columnIndex} does not exist in this layout`);
      return;
    }

    widgets.forEach(widgetId => {
      if (!canPlaceWidget(widgetId, column.type)) {
        const widget = widgetRegistry[widgetId];
        errors.push(`"${widget?.name || widgetId}" cannot be placed in ${column.type} columns`);
      }
    });
  });

  return { valid: errors.length === 0, errors };
};
